import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MicroYearBars from '@/features/researcher/components/MicroYearBars';
import { highlightText } from '@/features/shared/lib/utils';
import { post } from '@/features/shared/lib/api';

export default function ResearcherCard({ item, linkMode = 'path', liClassName, yearDisplay = 'bars', yearRange, highlightQuery = '', onFavoriteChange, showFavoriteButton = true }) {
  const navigate = useNavigate();
  const id = item?.scholar_id ?? item?.id;

  // favor_id 상태 관리 (초기값은 item에서 가져오거나 null)
  const [favorId, setFavorId] = useState(item?.favor_id || null);
  const [isFavorite, setIsFavorite] = useState(!!item?.favor_id);

  const to = linkMode === 'query' ? `/researchers/?id=${encodeURIComponent(id)}` : `/researchers/${encodeURIComponent(id)}`;

  const onCardClick = useCallback((e) => {
    if (!id) return;

    // Check for Cmd (Mac) or Ctrl (Windows) key to open in a new tab
    if (e && (e.metaKey || e.ctrlKey)) {
      let homeUrl = (typeof rmVars !== 'undefined' && rmVars.homeUrl) ? rmVars.homeUrl : '';
      // If we are on port 3000 (dev mode), ensure homeUrl also uses port 3000
      if (typeof window !== 'undefined' && window.location.port === '3000' && homeUrl && !homeUrl.includes(':3000')) {
        homeUrl = homeUrl.replace(/(\/\/[^\/]+)/, '$1:3000');
      }
      const newTabUrl = `${homeUrl}/researchers/${encodeURIComponent(id)}`;
      window.open(newTabUrl, '_blank');
      return;
    }

    navigate(to);
    // ensure scroll to top on route change
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        // slight delay to allow route transition, then scroll
        setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 0);
      }
    } catch (e) {
      // no-op
    }
  }, [id, to, navigate]);

  const profileSrc = (() => {
    const src = item?.profile_pic;
    if (src && String(src).trim() !== '') return src;
    return `${rmVars.themeUrl}images/profile03.png`;
  })();

  // const nameText = item?.scholar_name_eng || item?.name_kor || item?.name || '—';
  const nameText = (() => {
    const eng = item?.scholar_name_eng;
    const kor = item?.scholar_name_kor;
    const name = item?.scholar_name;

    if (eng && kor) {
      return `${eng}(${kor})`;
    } else if (eng && !kor) {
      // eng 있고 kor 없을 때 scholar_name이 있으면 괄호에 넣고, 없으면 eng만 표시
      return name ? `${eng}(${name})` : eng;
    } else if (!eng && kor) {
      return kor;
    } else if (!eng && !kor && name) {
      return name;
    }

    return item?.name || '—';
  })();

  const dept = item?.dept_name || item?.maj_name || '';
  const college = item?.col_name || '';
  const subText = [dept, college].filter(v => v && String(v).trim() !== '').join(' · ');

  const keywords = Array.isArray(item?.keywords) ? item.keywords : [];

  let totalCount = 0;
  let totalCitation = 0;
  let yearData = { start: null, end: null, maxBar: 0, data: [] }; // 초기값 설정
  if (item?.publications_by_year) {
    const pby = item.publications_by_year;

    // 1. yearRange 저장
    yearData.start = pby.start_year;
    yearData.end = pby.end_year;
    yearData.data = pby.data || [];

    // 2. 합계 계산 (data가 배열인 경우)
    if (Array.isArray(pby.data)) {
      yearData.maxBar = pby.data.length;
      const totals = pby.data.reduce((acc, curr) => {
        acc.citationTotal += parseInt(curr.citation || 0, 10);
        acc.countTotal += parseInt(curr.count || 0, 10);
        return acc;
      }, { citationTotal: 0, countTotal: 0 });

      totalCitation = totals.citationTotal;
      totalCount = totals.countTotal;
    }
  }
  const docCount = (totalCount || item?.resource_count || 0).toLocaleString();
  const citeCount = totalCitation.toLocaleString();
  const hIndex = item?.meta?.hindex || 0;

  // const docCount = item?.resource_count ?? item?.scholarly_output ?? item?.publication_count;
  // const citeCount = item?.citation_count ?? item?.citedby_count ?? item?.citations;
  // const hIndex = item?.h_index ?? item?.hindex;

  const hasWt2 = Array.isArray(item?.meta?.wt2) && item.meta.wt2.length > 0;
  const hasHcr = Array.isArray(item?.meta?.hcr) && item.meta.hcr.length > 0;

  // item의 favor_id가 변경되면 상태 업데이트
  useEffect(() => {
    if (item?.favor_id) {
      setFavorId(item.favor_id);
      setIsFavorite(true);
    } else {
      setFavorId(null);
      setIsFavorite(false);
    }
  }, [item?.favor_id]);

  // 선호 연구자 추가/삭제 핸들러
  const handleFavoriteClick = useCallback(async (e) => {
    e.stopPropagation(); // 카드 클릭 이벤트 방지

    // rmVars.isLogin 체크
    const isLoggedIn = typeof rmVars !== 'undefined' && rmVars?.isLogin == true;

    // false일 때: 로그인 메시지 표시
    if (!isLoggedIn) {
      alert('로그인후 이용해주세요.');
      return;
    }

    // true일 때: item?.favor_id 유무에 따라 update/delete 구분
    const currentUserId = typeof rmVars !== 'undefined' && rmVars?.scholar_id ? rmVars.scholar_id : null;
    if (!currentUserId || !id) return;

    // item?.favor_id가 있으면 delete, 없으면 update
    const hasFavorId = item?.favor_id || favorId;

    try {
      if (hasFavorId) {
        // 삭제 API 호출 (POST 메서드 사용)
        const favorIdToDelete = item?.favor_id || favorId;
        const response = await post(`/rm/v1/scholar_favorite/${currentUserId}/delete`, {}, {
          params: { favor_id: favorIdToDelete }
        });

        if (response?.status && response?.result?.success === true) {
          setFavorId(null);
          setIsFavorite(false);
          // 부모 컴포넌트에 업데이트 알림 (옵션)
          if (item && typeof item === 'object') {
            delete item.favor_id;
          }
          // 부모 컴포넌트에 콜백 호출 (데이터 갱신 필요 시)
          if (typeof onFavoriteChange === 'function') {
            onFavoriteChange({ type: 'delete', scholarId: id, favorId: favorIdToDelete });
          }
        }
      } else {
        // 추가 확인
        // if (!window.confirm('선호 연구자로 추가하시겠습니까?')) {
        //   return;
        // }

        // 추가 API 호출
        const response = await post(`/rm/v1/scholar_favorite/${currentUserId}/update`, {}, {
          params: { favor_scholar_id: id }
        });

        if (response?.status && response?.result?.success === true && response?.result?.favor_id) {
          setFavorId(response.result.favor_id);
          setIsFavorite(true);
          // 부모 컴포넌트에 업데이트 알림 (옵션)
          if (item && typeof item === 'object') {
            item.favor_id = response.result.favor_id;
          }
          // 부모 컴포넌트에 콜백 호출 (데이터 갱신 필요 시)
          if (typeof onFavoriteChange === 'function') {
            onFavoriteChange({ type: 'add', scholarId: id, favorId: response.result.favor_id });
          }
        }
      }
    } catch (error) {
      console.error('선호 연구자 처리 중 오류:', error);
      alert('처리 중 오류가 발생했습니다.');
    }
  }, [item?.favor_id, favorId, id]);

  const defaultLiClassName = "relative p-8 pb-[14px] bg-white border border-[#E4E8F1] rounded-xl min-[800px]:w-[calc((100%-28px)/2)] xl:w-[calc((100%-56px)/3)] xl:px-[30px] xl:pt-[26px] xl:pb-4 2xl:w-[calc((100%-84px)/4)] hover:shadow-[0px_4px_20px_rgba(0,0,0,0.05)] tansition duration-300";

  return (
    <li className={liClassName || defaultLiClassName}>
      <div
        className="w-[61px] h-[61px] rounded-full overflow-hidden border border-[#E4E8F1] mb-3"
      >
        <img
          src={profileSrc}
          alt="photo" className="w-full h-full object-cover object-center"/>
      </div>

      <div className="text-[15px] font-bold leading-[140%] flex items-center hover:underline underline-offset-2 decoration-1 cursor-pointer" onClick={onCardClick}>
        {highlightQuery ? highlightText(nameText, highlightQuery) : nameText}
        {typeof rmVars !== 'undefined' && rmVars?.isEditor && id && (
          <a
            href={`${rmVars.homeUrl}/wp-admin/admin.php?page=scholar&record_id=${id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 ml-2"
            target="_blank"
            rel="noopener noreferrer"
            title="Edit Researcher"
          >
            <span
              className="editor-edit"
              style={{
                width: '18px',
                height: '18px',
                filter: 'invert(11%) sepia(70%) saturate(6625%) hue-rotate(348deg) brightness(88%) contrast(99%)',
                backgroundSize: '18px auto',
                display: 'inline-block',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundImage: `url(${rmVars.themeUrl}images/edit-regular.svg)`
              }}
            />
          </a>
        )}
      </div>
      <p className="mt-1 text-[12px] text-[#8D9AAF] leading-[140%]">
        {highlightQuery ? highlightText(subText || '\u00A0', highlightQuery) : (subText || '\u00A0')}
      </p>

      {(hasWt2 || hasHcr) && (
        <div className="flex items-center flex-wrap gap-1 mt-[10px]">
          {hasWt2 && (
            <div className="inline-flex items-center gap-x-1 px-[10px] py-[2px] text-[11px] leading-[140%] font-semibold text-[#F3652D] bg-[#FFE9E9] rounded-full">
              <img src={`${rmVars.themeUrl}images/world-top-ico.png`} className="w-3" alt="world top" />
              <span>World Top {typeof item?.meta?.wt2 === 'number' ? item.meta.wt2 : 2}%</span>
            </div>
          )}
          {hasHcr && (
            <div className="inline-flex items-center gap-x-1 px-[10px] py-[2px] text-[11px] leading-[140%] font-semibold text-[#8D6DE2] bg-[#EDE7FF] rounded-full">
              <img src={`${rmVars.themeUrl}images/hcr-ico.png`} className="w-3" alt="hcr" />
              <span>HCR</span>
            </div>
          )}
        </div>
      )}

      {keywords.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {keywords.slice(0, 5).map((kw, idx) => (
            <span key={idx} className="text-[11px] text-[#6C7687] bg-[#F3F5F9] px-1.5 py-0.5 rounded">
              #{highlightQuery ? highlightText(kw, highlightQuery) : kw}
            </span>
          ))}
        </div>
      )}

      <ul
        className="mt-4 px-5 border border-[#E4E8F1] rounded-[12px] text-[14px] text-[#6C7687] font-bold leading-[140%]"
      >
        <li
          className="flex justify-between items-center py-[10px] border-b border-b-[#E4E8F1] xl:py-[14px]"
        >
          <span>Scholarly Output</span>
          {docCount ? (
            <a href={`${rmVars.homeUrl}/publications/?scholar_id=${encodeURIComponent(id)}`} className="hover:underline underline-offset-2 decoration-1">
              <span className="text-[#376BDB]">{docCount}</span>
            </a>
          ) : (
            <span className="text-[#333]">—</span>
          )}
        </li>
        <li
          className="flex justify-between items-center py-[10px] border-b border-b-[#E4E8F1] xl:py-[14px]"
        >
          <span>Citation Count</span>
          <span className="text-[#333]">{citeCount ?? '—'}</span>
        </li>
        <li
          className="flex justify-between items-center py-[10px] xl:py-[14px]"
        >
          <span>H-Index</span>
          <span className="text-[#333]">{hIndex ?? '—'}</span>
        </li>
      </ul>

      {showFavoriteButton && (
        <div
          className="intrest flex-shrink-0 w-[38px] h-[38px] cursor-pointer group absolute top-8 right-9"
          onClick={handleFavoriteClick}
        >
          <img src={rmVars.themeUrl + 'images/intrest-default.png'} className={`interest-default group-hover:hidden ${favorId ? 'hidden' : ''}`} alt="interest-default" />
          <img src={rmVars.themeUrl + 'images/intrest-hover.png'} className={`interest-hover hidden group-hover:block ${favorId ? '!hidden' : ''}`} alt="interest-hover" />
          <img src={rmVars.themeUrl + 'images/intrest-active.png'} className={`interest-active ${favorId ? 'block' : 'hidden'}`} alt="interest-active" />
        </div>
      )}

      {yearDisplay === 'bars' ? (
        <MicroYearBars
          items={yearData.data}
          maxBars={yearData.maxBar}
        />
      ) : yearDisplay === 'text' ? (
        <div className="mt-6 flex justify-center gap-[10px] text-[12px] text-[#8D9AAF] leading-[140%]">
          <div>{yearData.start || ''}</div>
          <div className="graph-area"></div>
          <div>{yearData.end || ''}</div>
        </div>
      ) : null}
    </li>
  );
}
